import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useCarWashStore } from '../../contexts/mobx';
import { Modal, Table, Input, Form, Switch, Tooltip } from 'antd';
import { Button } from '../../components/button';
import style from './style.module.scss';
import { CreateButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';
import { SearchInput } from '../../components/search-input';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { useSearchParams } from '../../utils/use-search-params';
import {
    CarWashStore as CarWashStoreType,
    CarWashStoreInput,
} from '../../stores/car-wash';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { SortOrder } from 'antd/es/table/interface';
import { TEN_PER_PAGE } from '../../utils/const';

export const CarWashPage = observer(() => {
    const carWashStore = useCarWashStore();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingStore, setEditingStore] = useState<CarWashStoreType | null>(
        null
    );
    const [searchTerm, setSearchTerm] = useState('');
    const search = useSearchParams<{ page?: number; perPage?: number }>();

    React.useEffect(() => {
        carWashStore.fetchCarWashStores();
    }, [carWashStore]);

    const filteredStores = React.useMemo(() => {
        if (!searchTerm) return carWashStore.carwashStores || [];
        const searchLower = searchTerm.toLowerCase();
        return (carWashStore.carwashStores || []).filter(
            (store) =>
                store.storeNref.toLowerCase().includes(searchLower) ||
                store.city.toLowerCase().includes(searchLower) ||
                store.address.toLowerCase().includes(searchLower)
        );
    }, [carWashStore.carwashStores, searchTerm]);

    const page = search.params.page ?? 0;
    const perPage = search.params.perPage ?? TEN_PER_PAGE;
    const start = page * perPage;
    const paginatedStores = filteredStores.slice(start, start + perPage);

    const handleCreate = async (values: CarWashStoreInput) => {
        await carWashStore.createCarWashStore(values);
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleUpdate = async (values: CarWashStoreInput) => {
        if (editingStore) {
            await carWashStore.updateCarWashStore(
                editingStore.carwashStoreId,
                values
            );
            setEditingStore(null);
            setIsModalVisible(false);
            form.resetFields();
        }
    };

    const showCreateModal = () => {
        setEditingStore(null);
        setIsModalVisible(true);
    };

    const showEditModal = (store: CarWashStoreType) => {
        setEditingStore(store);
        form.setFieldsValue({
            storeNref: store.storeNref,
            address: store.address,
            city: store.city,
            isSoftTouch: store.isSoftTouch,
            isUltimate: store.isUltimate,
            isTouchFree: store.isTouchFree,
            isUltimatePlus: store.isUltimatePlus,
        });
        setIsModalVisible(true);
    };

    const columns = [
        {
            title: 'NREF',
            dataIndex: 'storeNref',
            key: 'storeNref',
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
        },
        {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
            render: (value: string) =>
                value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
            sorter: (a: CarWashStoreType, b: CarWashStoreType) =>
                a.city.localeCompare(b.city),
            defaultSortOrder: 'ascend' as SortOrder,
        },
        {
            title: 'Soft Touch',
            dataIndex: 'isSoftTouch',
            key: 'isSoftTouch',
            render: (value: boolean) => (value ? 'Yes' : 'No'),
            sorter: (a: CarWashStoreType, b: CarWashStoreType) =>
                Number(b.isSoftTouch) - Number(a.isSoftTouch),
        },
        {
            title: 'Touch Free',
            dataIndex: 'isTouchFree',
            key: 'isTouchFree',
            render: (value: boolean) => (value ? 'Yes' : 'No'),
            sorter: (a: CarWashStoreType, b: CarWashStoreType) =>
                Number(b.isTouchFree) - Number(a.isTouchFree),
        },
        {
            title: 'Ultimate',
            dataIndex: 'isUltimate',
            key: 'isUltimate',
            render: (value: boolean) => (value ? 'Yes' : 'No'),
            sorter: (a: CarWashStoreType, b: CarWashStoreType) =>
                Number(b.isUltimate) - Number(a.isUltimate),
        },
        {
            title: 'Ultimate+',
            dataIndex: 'isUltimatePlus',
            key: 'isUltimatePlus',
            render: (value: boolean) => (value ? 'Yes' : 'No'),
            sorter: (a: CarWashStoreType, b: CarWashStoreType) =>
                Number(b.isUltimatePlus) - Number(a.isUltimatePlus),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: CarWashStoreType) => (
                <Button
                    onClick={() => showEditModal(record)}
                    variant="primary"
                    size="middle"
                    icon={<EditOutlined />}
                >
                    Edit
                </Button>
            ),
        },
    ];

    return (
        <>
            <PageTopBar title="Car Wash">
                <CreateButton onClick={showCreateModal} />
            </PageTopBar>
            <div className="action_zone_above_table">
                <div className="table_header_options_bar">
                    <div className={'query_box'}>
                        <SearchInput
                            searchValue={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by NREF, City, or Address"
                            allowClear
                        />
                    </div>
                </div>
                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: filteredStores.length,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>
            <Table
                dataSource={paginatedStores}
                columns={columns}
                rowKey="carwashStoreId"
                loading={carWashStore.isFetchingStores}
                scroll={{ x: 900 }}
                pagination={false}
            />
            <Modal
                title={
                    editingStore ? 'Edit Car Wash Store' : 'Add Car Wash Store'
                }
                open={isModalVisible}
                className={style.carwash_modal}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    setEditingStore(null);
                }}
                footer={null}
            >
                <Form
                    form={form}
                    className={style.carwash_form}
                    initialValues={{
                        isSoftTouch: false,
                        isUltimate: false,
                        isTouchFree: false,
                        isUltimatePlus: false,
                    }}
                    onFinish={(values: unknown) => {
                        const formValues = values as CarWashStoreInput;
                        if (editingStore) {
                            handleUpdate(formValues);
                        } else {
                            handleCreate(formValues);
                        }
                    }}
                    layout="vertical"
                >
                    <Form.Item
                        name="storeNref"
                        label="Store NREF"
                        rules={[
                            {
                                required: true,
                                message:
                                    'Please enter store NREF with prefix(600....)',
                            },
                        ]}
                    >
                        <Input autoFocus />
                    </Form.Item>
                    <Form.Item
                        name="address"
                        label="Address"
                        rules={[
                            { required: true, message: 'Please enter address' },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="city"
                        label="City"
                        rules={[
                            { required: true, message: 'Please enter city' },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <div className={style.switch_row}>
                        <Form.Item
                            name="isSoftTouch"
                            label={<span>Soft Touch</span>}
                            valuePropName="checked"
                            className={style.switch_item}
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="isTouchFree"
                            label={
                                <span>
                                    Touch Free&nbsp;
                                    <Tooltip title="Contactless">
                                        <InfoCircleOutlined
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </Tooltip>
                                </span>
                            }
                            valuePropName="checked"
                            className={style.switch_item}
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="isUltimate"
                            label={<span>Ultimate</span>}
                            valuePropName="checked"
                            className={style.switch_item}
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="isUltimatePlus"
                            label={<span>Ultimate+</span>}
                            valuePropName="checked"
                            className={style.switch_item}
                        >
                            <Switch />
                        </Form.Item>
                    </div>
                    <Form.Item>
                        <Button
                            variant="primary"
                            htmlType="submit"
                            block
                            style={{ marginTop: '8px' }}
                        >
                            {editingStore ? 'Update' : 'Create'}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
});
